import React, { useState } from 'react';
import { 
  FiCopy as Copy, 
  FiShare as Share, 
  FiMail as Mail, 
  FiMessageSquare as MessageSquare, 
  FiGift as Gift, 
  FiUsers as Users, 
  FiDollarSign as DollarSign 
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { AGENCY_PHONE_DISPLAY } from '../constants/contact';

const ReferralView = () => {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  const referralCode = user?.referralCode || '—';
  const referralLink = user?.referralCode
    ? `https://pinoygeneralinsurance.com/ref/${user.referralCode}`
    : '';

  const handleCopyLink = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleShareSMS = () => {
    if (!referralLink) return;
    const message = `Hey! I've been using Pinoy General Insurance for my coverage and they're amazing. Check them out and save money on your insurance: ${referralLink}`;
    window.open(`sms:?body=${encodeURIComponent(message)}`, '_self');
  };

  const handleShareEmail = () => {
    if (!referralLink) return;
    const subject = 'Save Money on Insurance with Pinoy General Insurance';
    const body = `Hi there!\n\nI wanted to share something that's saved me money on insurance. I've been working with Pinoy General Insurance Services and they've been fantastic - great rates, excellent service, and they really understand our community.\n\nIf you're looking for auto, home, or business insurance, I highly recommend getting a quote from them. You can use my referral link below:\n\n${referralLink}\n\nThey've been serving the Filipino-American community in Southern California since 1993, and I think you'll love working with them too.\n\nBest regards,\n${user?.firstName}`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_self');
  };

  const rewardTiers = [
    {
      icon: Gift,
      type: 'Auto/Home Referral',
      reward: '$50 Amazon Gift Card',
      description: 'When your referral purchases auto or home insurance'
    },
    {
      icon: DollarSign,
      type: 'Commercial Referral',
      reward: '$100 Amazon Gift Card',
      description: 'When your referral purchases commercial insurance'
    },
    {
      icon: Users,
      type: '3 Referral Bonus',
      reward: 'Bonus $50',
      description: 'Extra reward when you reach 3 successful referrals'
    },
    {
      icon: Gift,
      type: '5 Referral Bonus',
      reward: 'Bonus $150',
      description: 'Extra reward when you reach 5 successful referrals'
    }
  ];

  // Sample referral stats (in real app, this would come from backend)
  const referralStats = {
    peopleReferred: 3,
    policiesSold: 1,
    rewardsEarned: 50,
    pendingRewards: 100
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#DC143C] to-[#B01030] rounded-xl text-white p-8">
        <div className="max-w-2xl">
          <h1 className="text-4xl font-bold mb-4">Share the Protection</h1>
          <p className="text-xl text-white/90 mb-6">
            Help your friends and family get great insurance rates while earning rewards for yourself
          </p>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <Users size={32} />
            </div>
            <div>
              <div className="text-sm text-white/80">Your Referral Code</div>
              <div className="text-2xl font-bold">{referralCode}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Referral Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="text-3xl font-bold text-[#DC143C] mb-1">{referralStats.peopleReferred}</div>
          <div className="text-sm text-gray-600">People Referred</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="text-3xl font-bold text-green-600 mb-1">{referralStats.policiesSold}</div>
          <div className="text-sm text-gray-600">Policies Sold</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="text-3xl font-bold text-[#FFD700] mb-1">${referralStats.rewardsEarned}</div>
          <div className="text-sm text-gray-600">Rewards Earned</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="text-3xl font-bold text-blue-600 mb-1">${referralStats.pendingRewards}</div>
          <div className="text-sm text-gray-600">Pending Rewards</div>
        </div>
      </div>

      {/* Referral Link Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Your Unique Referral Link</h2>
        
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 font-mono text-sm text-gray-700 break-all">
              {referralLink || <span className="text-gray-400 italic">Generating your link...</span>}
            </div>
            <button
              onClick={handleCopyLink}
              disabled={!referralLink}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                copied ? 'bg-green-100 text-green-700' : 'bg-[#DC143C] text-white hover:bg-[#B01030]'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <Copy size={16} /> {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>



        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button 
            onClick={handleShareSMS}
            className="flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
          >
            <MessageSquare size={20} /> Share via SMS
          </button>
          <button 
            onClick={handleShareEmail}
            className="flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            <Mail size={20} /> Share via Email
          </button>
          <button 
            onClick={handleCopyLink}
            className="flex items-center justify-center gap-2 border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            <Share size={20} /> Copy Link
          </button>
        </div>
      </div>

      {/* Reward Tiers */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Earn Rewards</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {rewardTiers.map((tier, index) => (
            <div key={index} className="p-6 bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-[#FFD700] rounded-full flex items-center justify-center">
                  <tier.icon size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{tier.type}</h3>
                  <div className="text-lg font-bold text-[#DC143C]">{tier.reward}</div>
                </div>
              </div>
              <p className="text-gray-600 text-sm">{tier.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">How It Works</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Share size={24} className="text-blue-600" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">1. Share Your Link</h3>
            <p className="text-gray-600 text-sm">
              Send your unique referral link to friends and family who need insurance
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users size={24} className="text-green-600" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">2. They Get a Quote</h3>
            <p className="text-gray-600 text-sm">
              Your referral gets a personalized quote and great rates on their insurance
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-[#FFD700] rounded-full flex items-center justify-center mx-auto mb-4">
              <Gift size={24} className="text-white" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">3. You Both Win</h3>
            <p className="text-gray-600 text-sm">
              They save money on insurance and you earn reward points for every successful referral
            </p>
          </div>
        </div>
      </div>

      {/* Terms */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">Referral Program Terms</h3>
        <div className="space-y-2 text-blue-800 text-sm">
          <p>• Referral rewards are issued after your referral's policy is active for 60 days</p>
          <p>• Rewards are sent via Amazon gift card to your registered email address</p>
          <p>• Self-referrals and duplicate referrals are not eligible for rewards</p>
          <p>• Referral program terms may change with 30 days notice</p>
          <p>• Contact us at {AGENCY_PHONE_DISPLAY} with any questions about the referral program</p>
        </div>
      </div>

      {/* Success Stories */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Success Stories</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#DC143C] rounded-full flex items-center justify-center text-white font-bold text-sm">
                M
              </div>
              <div>
                <div className="font-semibold text-gray-900">Maria Santos</div>
                <div className="text-sm text-gray-600">5 Referrals • $350 Earned</div>
              </div>
            </div>
            <p className="text-gray-700 text-sm">
              "I've helped 5 family members save money on their insurance and earned $350 in gift cards. It's a win-win!"
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#DC143C] rounded-full flex items-center justify-center text-white font-bold text-sm">
                J
              </div>
              <div>
                <div className="font-semibold text-gray-900">Jose Reyes</div>
                <div className="text-sm text-gray-600">3 Referrals • $200 Earned</div>
              </div>
            </div>
            <p className="text-gray-700 text-sm">
              "My friends were paying way too much for insurance. Now they're saving hundreds and I've earned rewards!"
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralView;